/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import { _ as e } from "../../../chunks/tslib.es6.js";
import "../../../intl.js";
import { watch as t } from "../../../core/reactiveUtils.js";
import { pt2px as s } from "../../../core/screenUtils.js";
import { property as i } from "../../../core/accessorSupport/decorators/property.js";
import "../../../core/accessorSupport/ensureType.js";
import "../../../core/arrayUtils.js";
import "../../../core/has.js";
import { subclass as a } from "../../../core/accessorSupport/decorators/subclass.js";
import { renderSVG as r } from "../../../symbols/support/svgUtils.js";
import { getCSSFilterFromEffectList as o } from "../../../symbols/support/utils.js";
import l from "../../Widget.js";
import { css as n } from "./CardCSS.js";
import { renderRelationshipRamp as c } from "./support/relationshipUtils.js";
import { getUnivariateAboveAndBelowRampElements as p, getUnivariateSizeRampSize as d, getUnivariateColorRampSize as m, getUnivariateColorRampPreview as h, getUnivariateColorRampMargin as y, getUnivariateColorSizeRampElements as g } from "./support/univariateUtils.js";
import { getTitle as v, attachToNode as f, isImageryStretchedLegend as u, getStretchStopLabel as b } from "../support/styleUtils.js";
import { Heading as _, incrementHeadingLevel as w } from "../../support/Heading.js";
import { accessibleHandler as L } from "../../support/decorators/accessibleHandler.js";
import { messageBundle as S } from "../../support/decorators/messageBundle.js";
import { tsx as x } from "../../support/jsxFactory.js";
import { isRTL as C } from "../../support/widgetUtils.js";
import { substitute as R } from "../../../intl/substitute.js";
const $ = 25,
  k = 25,
  z = 768,
  I = 100;
var N;
!function (e) {
  e.Auto = "auto", e.Stack = "stack", e.SideBySide = "side-by-side";
}(N || (N = {}));
const A = "#ddd",
  j = window.devicePixelRatio;
function E(e) {
  if (e) {
    if (e.type.includes("3d")) {
      const t = e.symbolLayers?.length;
      if (!t) return;
      const s = e.symbolLayers.at(t - 1),
        i = s.resource?.primitive;
      return "circle" === i || "cross" === i || "kite" === i || "sphere" === i || "cube" === i || "diamond" === i;
    }
    {
      const t = e.style;
      return "circle" === t || "diamond" === t || "cross" === t;
    }
  }
}
function F(e) {
  if (e) {
    if (e.type.includes("3d")) {
      const t = e.symbolLayers?.length;
      if (!t) return;
      const s = e.symbolLayers.at(t - 1).get("resource.primitive");
      return "triangle" === s || "cone" === s || "tetrahedron" === s;
    }
    return "triangle" === e.style;
  }
}
let T = class extends l {
  constructor(e, t) {
    super(e, t), this._hasIndicators = !1, this._selectedSectionName = null, this._sectionNames = [], this._sectionMap = new Map(), this.activeLayerInfos = null, this.headingLevel = 3, this.layout = N.Stack, this.messages = null, this.messagesCommon = null, this.type = "card", this.view = null;
  }
  initialize() {
    this.addHandles(t(() => this.activeLayerInfos, e => {
      this.removeAllHandles(), this._watchForSectionChanges(e);
    }));
  }
  render() {
    const {
      view: e
    } = this;
    this._hasIndicators = this.layout === N.Auto && e && e.container.clientWidth <= z || this.layout === N.Stack;
    const t = this.activeLayerInfos,
      s = t && t.toArray().map(e => this._renderLegendForLayer(e)).filter(e => !!e);
    this._hasIndicators ? this._selectedSectionName && this._sectionNames.includes(this._selectedSectionName) || (this._selectedSectionName = this._sectionNames && this._sectionNames[0]) : this._selectedSectionName = null;
    const i = this._sectionNames.length,
      a = this._sectionNames.map((e, t) => {
        const s = R(this.messagesCommon.pagination.pageText, {
          index: t + 1,
          total: i
        });
        return x("div", {
          "aria-controls": `${e}-panel`,
          "aria-label": s,
          "aria-selected": (this._selectedSectionName === e).toString(),
          bind: this,
          class: this.classes(n.indicator, {
            [n.activated]: this._selectedSectionName === e
          }),
          "data-section-name": e,
          id: e,
          key: e,
          onclick: this._selectSection,
          onkeydown: this._focusSection,
          role: "tab",
          tabIndex: this._selectedSectionName === e ? 0 : -1,
          title: s
        });
      }),
      r = this._hasIndicators && i > 1 ? x("div", {
        class: n.indicatorContainer,
        key: "carousel-navigation",
        role: "tablist"
      }, a) : null,
      o = this._hasIndicators ? this._sectionMap.get(this._selectedSectionName) : s && s.length ? s : null,
      l = {
        [n.stacked]: this._hasIndicators
      };
    return x("div", {
      class: this.classes(n.base, l)
    }, o || x("div", {
      class: n.message
    }, this.messages.noLegend), r);
  }
  _selectSection(e) {
    const t = e.target.getAttribute("data-section-name");
    t && (this._selectedSectionName = t);
  }
  _focusSection(e) {
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowRight":
        this._switchSectionOnArrowPress(e);
        break;
      case "Enter":
      case " ":
        this._selectSection(e);
    }
  }
  _switchSectionOnArrowPress(e) {
    const t = e.key,
      s = "ArrowLeft" === t ? -1 : 1,
      i = e.target.getAttribute("data-section-name"),
      a = this._sectionNames.indexOf(i),
      r = this._sectionNames;
    let o = null;
    -1 !== a && (r[a + s] ? o = document.getElementById(r[a + s]) : "ArrowLeft" === t ? o = document.getElementById(r[r.length - 1]) : "ArrowRight" === t && (o = document.getElementById(r[0])), o?.focus());
  }
  _watchForSectionChanges(e) {
    if (this._generateSectionNames(), e) {
      e.forEach(e => {
        const s = `activeLayerInfo-${e.layer.uid}-version-change`;
        this.removeHandles(s), this._watchForSectionChanges(e.children), this.addHandles(t(() => e.version, () => this._generateSectionNames()), s);
      });
      const s = "activeLayerInfos-collection-change";
      this.removeHandles(s), this.addHandles(e.on("change", () => this._watchForSectionChanges(e)), s);
    }
  }
  _generateSectionNames() {
    this._sectionNames.length = 0, this._selectedSectionName = null, this.activeLayerInfos && this.activeLayerInfos.forEach(this._generateSectionNamesForActiveLayerInfo, this);
  }
  _getSectionName(e, t, s) {
    return `${this.id}${e.uid}-type-${t.type}-${s}`;
  }
  _generateSectionNamesForActiveLayerInfo(e) {
    e.children.forEach(this._generateSectionNamesForActiveLayerInfo, this), e.legendElements && e.legendElements.forEach((t, s) => {
      this._sectionNames.push(this._getSectionName(e.layer, t, s));
    });
  }
  _renderLegendForLayer(e) {
    if (!e.ready) return null;
    if (e.children.length) {
      const t = e.children.map(e => this._renderLegendForLayer(e)).toArray();
      return x("div", {
        class: this.classes(n.service, n.groupLayer),
        key: e.layer.uid
      }, x("div", {
        class: n.serviceCaptionContainer
      }, e.title), t);
    }
    {
      const t = e.legendElements;
      if (t && !t.length) return null;
      const s = t.some(e => "relationship-ramp" === e.type),
        i = t.map((t, i) => this._renderLegendForElement(t, e, i, s)).filter(e => !!e);
      if (!i.length) return null;
      const a = {
        [n.groupLayerChild]: !!e.parent
      };
      return x("div", {
        class: this.classes(n.service, a),
        key: e.layer.uid
      }, x("div", {
        class: n.serviceCaptionContainer
      }, x("div", {
        class: n.serviceCaptionText
      }, e.title)), x("div", {
        class: n.serviceContent
      }, i));
    }
  }
  _renderLegendForElement(e, t, s, i = !1, a = !1) {
    const r = "color-ramp" === e.type,
      o = "opacity-ramp" === e.type,
      l = t.layer;
    let p = null;
    if ("string" == typeof e.title) p = e.title;else if (e.title) {
      const t = e.title,
        s = v(this.messages, t, r || o);
      p = t.title ? `${t.title} (${s})` : s;
    }
    const d = this._getSectionName(l, e, s),
      m = this._hasIndicators && !a ? x("div", null, x(_, {
        class: n.carouselTitle,
        level: this.headingLevel
      }, t.title), x(_, {
        class: n.layerCaption,
        level: w(this.headingLevel)
      }, p)) : p ? x(_, {
        class: n.layerCaption,
        level: this.headingLevel
      }, p) : null,
      h = t.effectList;
    let y = null;
    switch (e.type) {
      case "symbol-table":
        {
          const s = e.infos.map((s, i) => this._renderLegendForElementInfo(s, t, e.legendType, i)).filter(e => !!e);
          if (s.length) {
            const e = s[0].properties.classes?.[n.symbolRow],
              t = {
                [n.labelContainer]: !e && !i,
                [n.relationshipLabelContainer]: i
              };
            y = x("div", {
              class: this.classes(t)
            }, s);
          }
          break;
        }
      case "color-ramp":
      case "opacity-ramp":
      case "heatmap-ramp":
      case "stretch-ramp":
        y = this._renderLegendForRamp(e, l.opacity, h);
        break;
      case "size-ramp":
        y = this._renderSizeRamp(e, l.opacity);
        break;
      case "pie-chart-ramp":
        y = this._renderPieChartRamp(e);
        break;
      case "relationship-ramp":
        y = c(e, this.id, {
          opacity: l.opacity,
          effectList: h,
          ariaLabel: this.messages.previewRelationshipRampAriaLabel
        });
        break;
      case "univariate-above-and-below-ramp":
        y = this._renderUnivariateAboveAndBelowRamp(e, l.opacity, h);
        break;
      case "univariate-color-size-ramp":
        y = this._renderUnivariateColorSizeRamp(e, l.opacity, h);
    }
    if (!y) return null;
    const g = x("div", {
      "aria-labelledby": d,
      class: n.section,
      id: `${d}-panel`,
      key: d,
      role: "tabpanel",
      tabIndex: 0
    }, [m, y]);
    return a || this._sectionMap.set(d, g), g;
  }
  _renderPieChartRamp(e) {
    return x("div", {
      afterCreate: f,
      bind: e.preview,
      class: n.pieChartRampPreview
    });
  }
  _renderUnivariateAboveAndBelowRamp(e, t, s) {
    const {
      sizeRampElement: i,
      colorRampElement: a
    } = p(e, t, "horizontal");
    if (!i) return null;
    const r = d(i, "full", !0, "horizontal"),
      o = m(i, "above", !0, "horizontal"),
      l = m(i, "below", !0, "horizontal"),
      c = 12,
      g = this.messages?.previewColorRampAriaLabel,
      v = h(a, {
        width: o,
        height: c,
        rampAlignment: "horizontal",
        opacity: t,
        type: "above",
        effectList: s,
        ariaLabel: g
      }),
      u = h(a, {
        width: l,
        height: c,
        rampAlignment: "horizontal",
        opacity: t,
        type: "below",
        effectList: s,
        ariaLabel: g
      }),
      b = y(i, "card"),
      _ = i.infos.map(e => e.label),
      w = _.length - 1,
      L = _.map((e, t) => 0 === t || t === w ? x("div", {
        key: t
      }, e) : null),
      S = {
        display: "flex",
        flexDirection: "column"
      },
      R = {
        display: "flex",
        flexDirection: "row"
      },
      $ = {
        marginTop: "3px",
        display: "flex"
      };
    C(this.container) ? $.marginRight = `${b}px` : $.marginLeft = `${b}px`;
    const k = {
      width: `${r}px`,
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between"
    };
    return x("div", {
      class: n.layerRow,
      key: "size-ramp-preview",
      styles: S
    }, x("div", {
      class: this.classes(n.symbolContainer, n.sizeRampHorizontal),
      styles: R
    }, i.infos.map((e, t) => x("div", {
      afterCreate: f,
      bind: e.preview,
      class: n.symbol,
      key: t
    }))), v ? x("div", {
      class: n.univariateAboveAndBelowColorRamp,
      key: "color-ramp-preview",
      styles: $
    }, x("div", {
      afterCreate: f,
      bind: v
    }), x("div", {
      afterCreate: f,
      bind: u
    })) : null, x("div", {
      class: n.layerInfo
    }, x("div", {
      class: n.rampLabelsContainer,
      styles: k
    }, L)));
  }
  _renderUnivariateColorSizeRamp(e, t, s) {
    const {
      sizeRampElement: i,
      colorRampElement: a
    } = g(e, "horizontal");
    if (!i) return null;
    const r = d(i, "full", !1, "horizontal"),
      o = m(i, "full", !1, "horizontal"),
      l = h(a, {
        width: o,
        height: 12,
        rampAlignment: "horizontal",
        opacity: t,
        type: "full",
        effectList: s,
        ariaLabel: this.messages?.previewColorRampAriaLabel
      }),
      c = y(i, "card"),
      p = i.infos.length - 1,
      v = i.infos.map((e, t) => 0 === t || t === p ? x("div", {
        key: t
      }, e.label) : null),
      u = {
        display: "flex",
        flexDirection: "column"
      },
      b = {
        display: "flex",
        flexDirection: "row"
      },
      _ = {
        marginTop: "3px",
        display: "flex"
      };
    C(this.container) ? _.marginRight = `${c}px` : _.marginLeft = `${c}px`;
    const w = {
      width: `${r}px`,
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between"
    };
    return x("div", {
      class: n.layerRow,
      key: "size-ramp-preview",
      styles: u
    }, x("div", {
      class: this.classes(n.symbolContainer, n.sizeRampHorizontal),
      styles: b
    }, i.infos.map((e, t) => x("div", {
      afterCreate: f,
      bind: e.preview,
      class: n.symbol,
      key: t
    }))), x("div", {
      class: n.univariateAboveAndBelowColorRamp,
      key: "color-ramp-preview",
      styles: _
    }, x("div", {
      afterCreate: f,
      bind: l
    })), x("div", {
      class: n.layerInfo
    }, x("div", {
      class: n.rampLabelsContainer,
      styles: w
    }, v)));
  }
  _renderLegendForElementInfo(e, t, s, i) {
    const a = t.layer;
    if (e.type) return this._renderLegendForElement(e, t, i, !1, !0);
    const r = u(a, s),
      l = v(this.messages, e.label, !1) ?? "";
    if (e.preview) {
      if (!e.symbol?.type.includes("simple-fill")) {
        if (!e.label) return x("div", {
          afterCreate: f,
          bind: e.preview,
          key: i
        });
        const t = {
          [n.symbolCell]: this._hasIndicators
        };
        return x("div", {
          class: this.classes(n.layerRow, {
            [n.symbolRow]: this._hasIndicators
          }),
          key: i
        }, x("div", {
          afterCreate: f,
          bind: e.preview,
          class: this.classes(t)
        }), x("div", {
          class: this.classes(n.imageLabel, {
            [n.labelCell]: this._hasIndicators
          })
        }, l));
      }
      let s = 255,
        r = 255,
        c = 255,
        p = 0,
        d = 255,
        m = 255,
        h = 255,
        y = 0;
      const g = e.symbol.color?.a,
        v = e.symbol.outline?.color?.a;
      g && (s = e.symbol.color.r, r = e.symbol.color.g, c = e.symbol.color.b, p = e.symbol.color.a * a.opacity), v && (d = e.symbol.outline.color.r, m = e.symbol.outline.color.g, h = e.symbol.outline.color.b, y = e.symbol.outline.color.a * a.opacity);
      const u = e.symbol.color?.isBright ?? !0,
        b = u ? "rgba(255, 255, 255, .6)" : "rgba(0, 0, 0, .6)",
        _ = {
          background: g ? `rgba(${s}, ${r}, ${c}, ${p})` : "none",
          color: u ? "black" : "white",
          textShadow: `-1px -1px 0 ${b},\n                                              1px -1px 0 ${b},\n                                              -1px 1px 0 ${b},\n                                              1px 1px 0 ${b}`,
          border: v ? `1px solid rgba(${d}, ${m}, ${h}, ${y})` : "none",
          filter: o(t.effectList) ?? void 0
        };
      return x("div", {
        class: n.layerRow,
        key: i
      }, x("div", {
        class: n.labelElement,
        styles: _
      }, l));
    }
    if (e.src) {
      const t = this._renderImage(e, a, r);
      return x("div", {
        class: n.layerRow,
        key: i
      }, t, x("div", {
        class: n.imageLabel
      }, l));
    }
  }
  _renderImage(e, t, s) {
    const {
        label: i,
        src: a,
        opacity: r
      } = e,
      o = {
        [n.imageryLayerStretchedImage]: s,
        [n.symbol]: !s
      },
      l = {
        opacity: `${null != r ? r : t.opacity}`
      };
    return x("img", {
      alt: v(this.messages, i, !1),
      "aria-label": v(this.messages, i, !1),
      border: 0,
      class: this.classes(o),
      height: e.height,
      src: a,
      styles: l,
      width: e.width
    });
  }
  _renderSizeRampLines(e) {
    const t = e.infos,
      i = t[0],
      a = t[t.length - 1],
      r = i.symbol,
      o = this._hasIndicators,
      l = s(i.size + i.outlineSize) * j,
      n = s(a.size + a.outlineSize) * j,
      c = o ? l : l + 50 * j,
      p = o ? l / 2 + 50 * j : l,
      d = F(r),
      m = E(r),
      h = document.createElement("canvas");
    h.width = c, h.height = p, h.style.width = h.width / j + "px", h.style.height = h.height / j + "px";
    const y = h.getContext("2d");
    if (o) {
      y.beginPath();
      const e = 0,
        t = 0,
        s = c / 2 - n / 2,
        i = p;
      y.moveTo(e, t), y.lineTo(s, i);
      const a = c,
        r = 0,
        o = c / 2 + n / 2,
        l = p;
      y.moveTo(a, r), y.lineTo(o, l);
    } else {
      y.beginPath();
      const e = 0,
        t = p / 2 - n / 2,
        s = c,
        i = 0;
      y.moveTo(e, t), y.lineTo(s, i);
      const a = 0,
        r = p / 2 + n / 2,
        o = c,
        l = p;
      y.moveTo(a, r), y.lineTo(o, l);
    }
    return y.strokeStyle = A, y.stroke(), x("div", {
      afterCreate: f,
      bind: h,
      styles: o ? {
        display: "flex",
        marginTop: `-${d ? 0 : m ? l / 2 : 0}px`,
        marginBottom: `-${d ? n : m ? n / 2 : 0}px`
      } : {
        display: "flex",
        marginRight: `-${d ? 0 : m ? l / 2 : 0}px`,
        marginLeft: `-${d ? 0 : m ? n / 2 : 0}px`
      }
    });
  }
  _renderSizeRamp(e, t) {
    const s = e.infos,
      i = s[0].label,
      a = s[s.length - 1].label;
    let r = s[0].preview,
      o = s[s.length - 1].preview;
    const l = this._hasIndicators,
      c = {
        "flex-direction": l ? "column" : "row-reverse"
      };
    r && (r = r.cloneNode(!0), r.style.display = "flex"), o && (o = o.cloneNode(!0), o.style.display = "flex");
    const p = {
      opacity: null != t ? `${t}` : ""
    };
    return x("div", {
      class: this.classes(n.layerRow, {
        [n.sizeRampRow]: l
      })
    }, x("div", {
      class: n.rampLabel
    }, l ? i : a), x("div", {
      class: n.sizeRampContainer,
      styles: c
    }, x("div", {
      afterCreate: f,
      bind: r,
      class: n.sizeRampPreview,
      styles: p
    }), this._renderSizeRampLines(e), x("div", {
      afterCreate: f,
      bind: o,
      class: n.sizeRampContainer,
      styles: p
    })), x("div", {
      class: n.rampLabel
    }, l ? a : i));
  }
  _getRampStopLabel(e, t) {
    switch (t) {
      case "heatmap-ramp":
        return this.messages[e.label];
      case "stretch-ramp":
        return b(e, this.messages);
      default:
        return e.label;
    }
  }
  _renderLegendForRamp(e, t, s) {
    const i = e.infos,
      a = e.type,
      l = "heatmap-ramp" === a,
      c = i.length - 1,
      p = k,
      d = c > 2 && !l ? $ * c : I,
      m = d + 20,
      h = 10,
      y = i.slice(0).reverse(),
      g = y.length - 1,
      v = y.length % 2 != 0 ? y[y.length / 2 | 0] : null,
      f = v && x("div", {
        class: n.intervalSeparatorsContainer
      }, x("div", {
        class: n.intervalSeparator
      }, "|"), x("div", {
        class: n.rampLabel
      }, this._getRampStopLabel(v, a))),
      u = [[{
        shape: {
          type: "path",
          path: `M0 ${p / 2} L${h} 0 L${h} ${p} Z`
        },
        fill: y[0].color,
        stroke: {
          width: 0
        }
      }, {
        shape: {
          type: "rect",
          x: h,
          y: 0,
          width: d,
          height: p
        },
        fill: {
          type: "linear",
          x1: h,
          y1: 0,
          x2: d + h,
          y2: 0,
          colors: y.map((e, t) => ({
            color: e.color,
            offset: l && "ratio" in e ? e.ratio : t / c
          }))
        },
        stroke: {
          width: 0
        }
      }, {
        shape: {
          type: "path",
          path: `M${d + h} 0 L${m} ${p / 2} L${d + h} ${p} Z`
        },
        fill: y[g].color,
        stroke: {
          width: 0
        }
      }]],
      b = r(u, m, p),
      _ = {
        filter: o(s) ?? void 0,
        opacity: null == t ? void 0 : `${t}`
      },
      w = {
        justifyContent: "center"
      };
    return x("div", {
      class: n.layerRow,
      styles: w
    }, x("div", {
      class: n.rampLabel
    }, this._getRampStopLabel(y[0], a)), x("div", {
      class: n.symbolContainer
    }, x("div", {
      styles: _
    }, b), f), x("div", {
      class: n.rampLabel
    }, this._getRampStopLabel(y[g], a)));
  }
};
e([i()], T.prototype, "activeLayerInfos", void 0), e([i()], T.prototype, "headingLevel", void 0), e([i()], T.prototype, "layout", void 0), e([i(), S("esri/widgets/Legend/t9n/Legend")], T.prototype, "messages", void 0), e([i(), S("esri/t9n/common")], T.prototype, "messagesCommon", void 0), e([i({
  readOnly: !0
})], T.prototype, "type", void 0), e([i()], T.prototype, "view", void 0), e([L()], T.prototype, "_selectSection", null), T = e([a("esri.widgets.Legend.styles.Card")], T);
const U = T;
export { U as default };